/** @jsx isolateComponent */

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { SkeletonBlock } from '../SkeletonBlock';

type Props = {
  /**
   * Integer indicating the number of lines of text to render.
   */
  lines: number;
  /**
   * The size of type to render. Intentionally limited.
   */
  size: 'heading' | 'body';
  /**
   * Any of these CSS `text-align` values.
   */
  textAlign: 'left' | 'right' | 'center' | 'justify';
  /**
   * Any valid CSS `width` value.
   */
  width: number | string;
};

const Styles = labelStyles('SkeletonText', {
  root: (props: Props) => [
    {
      display: 'grid',
      gridTemplateColumns: '100%',
      gap: 5,
    },
    withModifier(props.textAlign, {
      justifyItems: {
        left: 'start',
        right: 'end',
        center: 'center',
        justify: 'left',
      }[props.textAlign],
    }),
  ],
});

const getLineHeight = (props: Props) =>
  ({
    heading: 30,
    body: 15,
  }[props.size]);

const getLineWidth = (props: Props, idx: number) => {
  // Single lines should always be 100% wide.
  if (idx === 0 && props.lines === 1) {
    return '100%';
  }
  // The last non-solo line should always be 70% wide.
  if (idx === props.lines - 1) {
    return '70%';
  }
  // Justified non-last lines should always be 100% wide.
  if (props.textAlign === 'justify') {
    return '100%';
  }
  // Any other lines should alternate in width to rag them.
  return idx % 2 ? '100%' : '93%';
};

export const SkeletonText = (props: Props) => {
  const blocks = Array.from({ length: props.lines }).map((_, idx) => {
    return (
      <SkeletonBlock
        height={getLineHeight(props)}
        key={idx}
        width={getLineWidth(props, idx)}
      />
    );
  });
  const style = {
    width: props.width,
  };
  return (
    <div css={Styles.root(props)} data-testid="SkeletonText" style={style}>
      {blocks}
    </div>
  );
};

SkeletonText.defaultProps = {
  size: 'body',
  textAlign: 'left',
  width: '100%',
};
